import ArticleCard from "@/components/ui/ArticleCard";
import { toNewsArticle } from "@/lib/articleAdapter";
import { Link } from "@/lib/router-compat";
import { ArrowUpRight } from "lucide-react";
import type { WeeklyBriefTake } from "@/services/weeklyBriefService";
import { SectionLabel } from "./SectionLabel";
import { StoryTile } from "./StoryTile";
import { T } from "@/i18n/T";

interface Article {
  id: string;
  title: string;
  excerpt: string | null;
  source_id: string | null;
  source_name?: string | null;
  published_at: string;
  image_url?: string | null;
}

const day = (s?: string) =>
  s ? new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";

function Meta({ a }: { a: Article }) {
  return (
    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
      <span className="truncate">{a.source_name || a.source_id || "Byteline"}</span>
      <span className="opacity-40">·</span>
      <span>{day(a.published_at)}</span>
    </div>
  );
}

export function TopStories({ articles, takes }: { articles: Article[]; takes: WeeklyBriefTake[] }) {
  if (!articles.length) return null;
  const takeMap = new Map(takes.map((t) => [t.id, t.take]));
  const [lead, ...rest] = articles;

  return (
    <section className="mb-16">
      <SectionLabel><T k="auto.top-stories-1fa6" /></SectionLabel>

      <Link
        to={`/article/${(lead as any).slug || lead.id}`}
        className="group mb-4 grid overflow-hidden rounded-3xl border border-border bg-card transition-colors hover:border-foreground/30 md:grid-cols-2"
      >
        <StoryTile
          index={1}
          title={lead.title}
          image={lead.image_url}
          w={900}
          h={620}
          className="h-56 w-full object-cover md:h-full md:min-h-[320px]"
        />
        <div className="flex flex-col justify-center gap-3 p-6 md:p-8">
          <div className="flex items-center gap-3">
            <span className="font-display text-2xl text-muted-foreground">01</span>
            <Meta a={lead} />
          </div>
          <h3 className="font-display text-2xl leading-tight text-foreground transition-colors group-hover:text-primary md:text-3xl">
            {lead.title}
          </h3>
          {takeMap.get(lead.id) ? (
            <p className="border-l-2 border-primary/50 pl-4 text-sm italic text-muted-foreground">
              {takeMap.get(lead.id)}
            </p>
          ) : (
            lead.excerpt && <p className="line-clamp-3 text-sm text-muted-foreground">{lead.excerpt}</p>
          )}
          <span className="mt-1 inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.18em] text-foreground">
            Read story <ArrowUpRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </Link>

      <div className="scrollbar-hide -mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 md:-mx-6 md:gap-4 md:px-6">
        {rest.map((a) => (
          <div key={a.id} className="w-[82vw] shrink-0 snap-start sm:w-[44vw] lg:w-[calc((100%-2rem)/3)]">
            <ArticleCard article={toNewsArticle(a)} />
          </div>
        ))}
      </div>
    </section>
  );
}
